import { type ApiErrorBody, PRELOAD_TTL_MS, preloadKey } from "@justone/shared";

declare global {
  interface Window {
    /** 서버가 index.html에 넣어 준 첫 화면 API 응답 (열쇠는 preloadKey) */
    __PRELOAD__?: Record<string, unknown>;
  }
}

let preloadUsed = false;

/** 미리 담긴 응답을 쓴 적이 있는지. 그 응답은 문서를 만든 때의 것이라, 실시간 연결이 처음 열리면 한 번 새로 받는다 */
export const usedPreload = () => preloadUsed;

/** 미리 담긴 응답이 있으면 한 번만 꺼내 쓴다 (페이지를 연 직후에만) */
function takePreloaded(method: string, url: string, body: unknown): { data: unknown } | null {
  const store = window.__PRELOAD__;
  if (!store) return null;
  const key = preloadKey(method, url, body);
  if (!(key in store)) return null;
  const data = store[key];
  delete store[key];
  if (performance.now() >= PRELOAD_TTL_MS) return null;
  preloadUsed = true;
  return { data };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields: Record<string, string> = {},
  ) {
    super(message);
  }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const form = body instanceof FormData;
  if (!form && (method === "GET" || method === "POST")) {
    const hit = takePreloaded(method, url, body);
    if (hit) return hit.data as T;
  }
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: "same-origin",
      headers: body !== undefined && !form ? { "content-type": "application/json" } : undefined,
      body: body === undefined ? undefined : form ? body : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "network", "서버에 연결할 수 없습니다. 네트워크를 확인하세요.");
  }
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const err = (data as ApiErrorBody | undefined)?.error;
    throw new ApiError(res.status, err?.code ?? "unknown", err?.message ?? `요청이 실패했습니다 (${res.status}).`, err?.fields);
  }
  return data as T;
}

export const http = {
  get: <T>(url: string) => request<T>("GET", url),
  post: <T>(url: string, body: unknown = {}) => request<T>("POST", url, body),
  patch: <T>(url: string, body: unknown) => request<T>("PATCH", url, body),
  put: <T>(url: string, body: unknown = {}) => request<T>("PUT", url, body),
  delete: <T>(url: string) => request<T>("DELETE", url),
  /** 본문이 있는 임의의 요청 (예: DELETE + JSON) */
  req: <T>(method: string, url: string, body?: unknown) => request<T>(method, url, body),
  /** multipart 업로드 (브라우저가 경계 문자열이 든 content-type을 붙인다) */
  upload: <T>(url: string, form: FormData) => request<T>("POST", url, form),
};

export function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : "알 수 없는 오류가 발생했습니다.";
}

export function fieldError(err: unknown, field: string): string | undefined {
  return err instanceof ApiError ? err.fields[field] : undefined;
}
